import Loader from "pages/Separate/Loader"
import React, { useEffect, useMemo, useState } from "react"
import ReactApexChart from "react-apexcharts"
import secureLocalStorage from "react-secure-storage"
import { Card, CardBody, Modal } from "reactstrap"
import { fetchNodeData, FetchNodePostData } from "utils/common-fetchData"
import Select from "react-select"
import DailyLinkedinReport from "./DailyLinkedinReport"

const VIEW_OPTIONS = [
  { label: "Current Month", value: 1 },
  { label: "3 Months", value: 3 },
  { label: "6 Months", value: 6 },
  { label: "1 Year", value: 12 },
]

const LinkedinReport = () => {
  const [loading, setLoading] = useState(false)
  const [series, setSeries] = useState([])
  const memoSeries = useMemo(() => series, [series])

  const [options, setOptions] = useState([])
  const [selection, setSelection] = useState([])
  const [viewRange, setViewRange] = useState(1)

  // FULL DATA STORE
  const [fullChartData, setFullChartData] = useState({
    raw: [],
    months: [],
    categories: [],
    series: [],
  })

  const [state, setState] = useState({
    options: {
      chart: {
        height: 350,
        type: "line",
        toolbar: { show: false },
      },
      dataLabels: { enabled: true },
      stroke: { curve: "smooth" },
      markers: { size: 3 },
      xaxis: { categories: [] },
      yaxis: { title: { text: "Followers" } },
      legend: { position: "top", horizontalAlign: "right" },
    },
  })

  const token = secureLocalStorage.getItem("auth_token")

  // 🔹 Fetch accounts
  const fetchAccounts = async () => {
    const res = await fetchNodeData(
      setLoading,
      `linkedin/report/${localStorage.getItem("auth_id")}`,
      token
    )
    console.log("clg: ", res)
    setOptions(
      Array.isArray(res?.data)
        ? res.data.map(i => ({ label: i.username, value: i.account }))
        : []
    )
  }

  // 🔹 Fetch report data
  const fetchODs = async () => {
    if (!selection.length) return

    const res = await FetchNodePostData(setLoading, "linkedin", token, {
      id: selection.map(s => s.value),
    })

    const apiData = res?.data?.data ?? []

    const monthSet = new Set()
    apiData.forEach(acc =>
      acc.data.forEach(d => {
        const dt = new Date(d.date)
        monthSet.add(`${dt.getFullYear()}-${dt.getMonth()}`)
      })
    )

    const months = Array.from(monthSet).sort(
      (a, b) => new Date(a + "-01") - new Date(b + "-01")
    )

    const categories = months.map(m => {
      const [y, mo] = m.split("-")
      return new Date(y, mo).toLocaleString("en-US", {
        month: "short",
        year: "2-digit",
      })
    })

    const seriesData = apiData.map(acc => {
      const map = new Map()
      acc.data.forEach(d => {
        const dt = new Date(d.date)
        map.set(`${dt.getFullYear()}-${dt.getMonth()}`, d.count)
      })

      return {
        name: acc.account_name || `Account ${acc.account}`,
        data: months.map(m => map.get(m) ?? null),
      }
    })

    setFullChartData({
      raw: apiData,
      months,
      categories,
      series: seriesData,
    })
  }

  // 🔹 APPLY VIEW LOGIC
  useEffect(() => {
    if (!fullChartData.raw.length) return

    // ✅ 1 MONTH VIEW → DAILY POINTS
    if (viewRange === 1) {
      const allDates = []
      fullChartData.raw.forEach(acc =>
        acc.data.forEach(d => allDates.push(new Date(d.date)))
      )

      const latest = new Date(Math.max(...allDates))
      const y = latest.getFullYear()
      const m = latest.getMonth()

      const dailyCategories = []
      const dailySeries = fullChartData.raw.map(acc => {
        const filtered = acc.data
          .filter(d => {
            const dt = new Date(d.date)
            return dt.getFullYear() === y && dt.getMonth() === m
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date))

        filtered.forEach(d => {
          if (!dailyCategories.includes(d.date)) {
            dailyCategories.push(d.date)
          }
        })

        return {
          name: acc.account_name || `Account ${acc.account}`,
          data: filtered.map(d => d.count),
        }
      })

      setSeries(dailySeries)

      setState(prev => ({
        ...prev,
        options: {
          ...prev.options,
          xaxis: {
            categories: dailyCategories.map(d =>
              new Date(d).toLocaleDateString("en-US", {
                day: "2-digit",
                month: "short",
              })
            ),
            title: { text: "Day" },
          },
        },
      }))
      return
    }

    // ✅ MULTI-MONTH VIEW
    const total = fullChartData.months.length
    const start = Math.max(total - viewRange, 0)

    setSeries(
      fullChartData.series.map(s => ({
        ...s,
        data: s.data.slice(start),
      }))
    )

    setState(prev => ({
      ...prev,
      options: {
        ...prev.options,
        xaxis: {
          categories: fullChartData.categories.slice(start),
          title: { text: "Month" },
        },
      },
    }))
  }, [viewRange, fullChartData])

  useEffect(() => {
    fetchAccounts()
  }, [])

  useEffect(() => {
    fetchODs()
  }, [selection])

  const [updateFollowersModal, setUpdateFollowersModal] = useState(false)
  const updateFollowers = () => {
    console.log("update followers clicked")
    setUpdateFollowersModal(true)
  }

  return (
    <div>
      {loading && <Loader />}

      <Card>
        <CardBody>
          {/* TITLE ROW */}
          <div className="mb-3 text-purple fs-5 fw-bold">LinkedIn Report</div>

          {/* FILTER ROW */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            {/* LEFT: ACCOUNT SELECT */}
            <div style={{ width: "400px" }}>
              <Select
                placeholder="Select Account"
                className="basic-multi-select"
                classNamePrefix="select"
                isMulti
                options={options}
                closeMenuOnSelect={false}
                value={selection}
                onChange={setSelection}
              />
            </div>

            {/* RIGHT: ACTIONS */}
            <div className="d-flex align-items-center gap-2">
              {/* BUTTON */}
              <a
                className="border-radius btn bg-purple"
                onClick={updateFollowers}
              >
                <i className="fas fa-plus me-2"></i>Update Followers
              </a>

              {/* VIEW SELECT */}
              <div style={{ width: "200px" }}>
                <Select
                  placeholder="Select View"
                  className="basic-multi-select"
                  classNamePrefix="select"
                  options={VIEW_OPTIONS}
                  value={VIEW_OPTIONS.find(v => v.value === viewRange)}
                  onChange={v => setViewRange(v.value)}
                />
              </div>
            </div>
          </div>

          <ReactApexChart
            options={state.options}
            series={series}
            type="line"
            height={350}
          />
        </CardBody>
      </Card>

      <Modal
        size="xl"
        isOpen={updateFollowersModal}
        centered
        toggle={() => {
          setUpdateFollowersModal(false)
        }}
      >
        <DailyLinkedinReport onClose={() => setUpdateFollowersModal(false)} />
      </Modal>
    </div>
  )
}

export default LinkedinReport
